export interface ICenteredScrollInput {
  /** The container's current `scrollTop`. */
  scrollTop: number;
  /** The container's full scrollable height, used to clamp at the bottom. */
  scrollHeight: number;
  /** The container's visible height. */
  containerHeight: number;
  /** The container's top edge, viewport-relative (`getBoundingClientRect().top`). */
  containerTop: number;
  /** The target element's top edge, viewport-relative. */
  elementTop: number;
  elementHeight: number;
}

/**
 * The `scrollTop` that brings `element` into view inside its own scroll
 * container, centered where it fits and top-aligned where it doesn't.
 *
 * This exists because `Element.scrollIntoView` scrolls *every* scrollable
 * ancestor, the document included — so centering a turn inside a transcript
 * pane also drags the whole page, and the header and rail visibly jump. Setting
 * one container's `scrollTop` moves only that container.
 *
 * An element taller than the container is aligned to the top rather than
 * centered: centering it would put its opening lines above the fold, which is
 * the part the operator is looking for.
 */
export function centeredScrollTop({
  scrollTop,
  scrollHeight,
  containerHeight,
  containerTop,
  elementTop,
  elementHeight,
}: ICenteredScrollInput): number {
  const centeringOffset = Math.max(0, (containerHeight - elementHeight) / 2);
  const next = scrollTop + (elementTop - containerTop) - centeringOffset;
  const maxScrollTop = Math.max(0, scrollHeight - containerHeight);
  return Math.min(Math.max(0, next), maxScrollTop);
}

export default centeredScrollTop;
